// Requirements audit panel — what the AI requirements cron has sent
// on the operator's behalf. Reads public.messages where
// source='requirements_reminder' (the rows the scan-requirements
// cron writes after each touch) and renders them most-recent first.
//
// Mirrors DeliveryAuditPanel pattern: lives in Settings as a
// transparency surface. Auto-hides when nothing's been sent.

import React from 'react';
import { supabase } from '../lib/supabase';

const LIMIT = 50;

function fmtAt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function statusBadge(status) {
  if (status === 'queued' || status === 'sent') {
    return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">SMS sent</span>;
  }
  if (status === 'sent_email') {
    return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">Email sent</span>;
  }
  if (status === 'dry_run') {
    return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-200">Dry-run</span>;
  }
  return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Failed</span>;
}

export default function RequirementsAuditPanel({ currentUser }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('messages')
        .select('id, lead_id, direction, body, twilio_sid, delivery_status, source, sent_at')
        .eq('source', 'requirements_reminder')
        .order('sent_at', { ascending: false })
        .limit(LIMIT);
      if (cancelled) return;
      setRows(Array.isArray(data) ? data : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  if (loading || rows.length === 0) return null;

  const smsCount   = rows.filter(r => r.delivery_status === 'queued' || r.delivery_status === 'sent').length;
  const emailCount = rows.filter(r => r.delivery_status === 'sent_email').length;
  const dryCount   = rows.filter(r => r.delivery_status === 'dry_run').length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-bold mb-1">AI requirements follow-up — recent activity</h3>
      <p className="text-sm text-slate-500 mb-3">
        Last {rows.length} reminders the AI sent for outstanding requirements. {smsCount} SMS, {emailCount} email, {dryCount} dry-run.
      </p>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {rows.map(r => {
          const clientId = String(r.lead_id || '').replace(/^client:/, '');
          const isEmail = r.delivery_status === 'sent_email';
          // Email rows store "[email] {subject}\n\n{body}" — show subject as the leading line.
          const subject = isEmail
            ? String(r.body || '').replace(/^\[email\] /, '').split('\n', 1)[0]
            : null;
          const preview = isEmail
            ? String(r.body || '').split('\n').slice(2).join('\n').slice(0, 140)
            : String(r.body || '').slice(0, 160);
          return (
            <div key={r.id} className="px-4 py-2.5 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  Client {clientId.slice(0, 8)}…
                  {subject && <span className="ml-1 text-slate-600 font-normal">— {subject}</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap line-clamp-2">{preview}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {statusBadge(r.delivery_status)}
                <span className="text-xs text-slate-500 tabular-nums">{fmtAt(r.sent_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
